import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Target, X } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const SOURCES = [
  { key: "reserver", label: "Réservation", color: "#0052FF" },
  { key: "contact", label: "Contact", color: "#F59E0B" },
  { key: "waitlist", label: "Liste d'attente", color: "#10B981" },
];

const fmtDay = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};
const fmtDayLong = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
};

export function GoogleLeadConversionsSection() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [source, setSource] = useState("");

  useEffect(() => {
    const params = { days };
    if (source) params.source = source;
    api.get("/admin/pixel-stats/google-lead-daily", { params })
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null));
  }, [days, source]);

  const visible = source ? SOURCES.filter((s) => s.key === source) : SOURCES;
  const bySource = data?.by_source || [];
  const maxSrc = bySource.reduce((m, s) => Math.max(m, s.count), 0);
  const srcLabel = SOURCES.find((s) => s.key === source)?.label;

  return (
    <section className="mt-8 rounded-lg border border-neutral-200 bg-white p-5" data-testid="google-lead-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold tracking-tight">
            <Target className="h-5 w-5 text-brand" /> Conversions « lead » envoyées à Google Ads
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            {"Conversion « Envoi de formulaire de lead » (1 €) envoyée à chaque réservation, message de contact ou inscription en liste d'attente (1 envoi = 1 conversion, heure de Paris)."}
          </p>
        </div>
        <div className="flex gap-1" data-testid="google-lead-range">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`google-lead-range-${d}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${days === d ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"}`}>
              {d} j
            </button>
          ))}
        </div>
      </div>
      {data && !data.configured && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="google-lead-not-configured">
          {"Libellé de conversion Google Ads « lead » vide : renseigne-le dans Réglages → Publicité pour envoyer les conversions."}
        </p>
      )}
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          [srcLabel ? `Aujourd'hui · ${srcLabel}` : "Aujourd'hui", data?.today, "google-lead-today"],
          [`${days} derniers jours${srcLabel ? ` · ${srcLabel}` : ""}`, data?.total_period, "google-lead-period"],
          ["Depuis le début", data?.total_all_time, "google-lead-all"],
        ].map(([label, val, tid]) => (
          <div key={tid} className="min-w-0 rounded-md bg-neutral-50 px-2.5 py-3 sm:px-3">
            <p className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-neutral-500 sm:text-[11px] sm:tracking-wider">{label}</p>
            <p className="mt-1 text-2xl font-extrabold tracking-tight" data-testid={tid}>{val ?? "—"}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 h-60 w-full" data-testid="google-lead-chart">
        {data?.series?.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11 }} minTickGap={12} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDayLong} cursor={{ fill: "rgba(0,82,255,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {visible.map((s, i) => (
                <Bar key={s.key} dataKey={s.key} name={s.label} stackId="lead" fill={s.color}
                  radius={i === visible.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="grid h-full place-items-center text-sm text-neutral-400">Chargement…</div>
        )}
      </div>

      <div className="mt-6 border-t border-neutral-100 pt-5" data-testid="google-lead-by-source">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-neutral-900">Par formulaire — {days} derniers jours</h3>
          {source && (
            <button onClick={() => setSource("")} data-testid="google-lead-source-reset"
              className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-bold text-white hover:bg-brand-hover">
              {srcLabel} <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-neutral-500">{"Clique sur un formulaire pour n'afficher que lui dans le graphique."}</p>
        <ul className="mt-3 space-y-1.5">
          {SOURCES.map((s) => {
            const row = bySource.find((b) => b.source === s.key) || { count: 0, share: 0 };
            const active = source === s.key;
            return (
              <li key={s.key}>
                <button onClick={() => setSource(active ? "" : s.key)} data-testid={`google-lead-source-${s.key}`}
                  className={`grid w-full grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors sm:grid-cols-[minmax(0,10rem)_1fr_auto] ${active ? "bg-brand/10 ring-1 ring-brand/40" : "hover:bg-neutral-50"}`}>
                  <span className="flex items-center gap-2 truncate font-semibold text-neutral-800">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />{s.label}
                  </span>
                  <span className="h-2.5 overflow-hidden rounded-full bg-neutral-100">
                    <span className="block h-full rounded-full" style={{ background: s.color, width: `${maxSrc && row.count ? Math.max(4, (row.count / maxSrc) * 100) : 0}%` }} />
                  </span>
                  <span className="whitespace-nowrap text-xs text-neutral-600">
                    <strong className="text-sm text-neutral-900" data-testid={`google-lead-source-${s.key}-count`}>{row.count}</strong> · {row.share}%
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
